/* eslint-disable max-len */
import React, {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import Lottie from 'lottie-web';
import styled, {createGlobalStyle} from 'styled-components';
import {Helmet} from 'react-helmet';

import {notWebMediaQuery, PageBodyWrapper} from './../styles';
import {capitalize, useGetUserToken} from './../helpers';

import YorhaBitmovin from '@tides/yorha-react';
import '@tides/yorha-react/dist/main.css';

import Button from './../components/Button';

import * as EpisodeActions from '../store/actions/episode';
import * as VideoActions from '../store/actions/video';
import * as DrmActions from '../store/actions/drm';
import {episodeStarted, episodeFinished} from '../store/actions/recommendation';

import Loader from '../components/Loader';
import PlayerError from '../components/PlayerError';

import CNLoader from '../assets/animations/cnito_loader_1.json';
import api, {graphqlQueryBuild} from '../services/api';

export const CartoonitoPlayer = props => {
    const history = useHistory();
    const params = useParams();
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const config = useSelector(state => state.config);

    props = {
        ...props,
        ...useSelector(state => {
            return {
                ...state.episode,
                ...state.show,
                ...state.drm,
                ...state.video,
            };
        }),
    };

    const user = useSelector(state => state.user);
    const idp = user && user.idp;
    const tve = user && user.user_type === 'tve';

    const userToken = useGetUserToken();

    const [isFocused, setIsFocused] = useState(false);
    const [pageState, setPageState] = useState('loading');
    const [currentShow, setCurrentShow] = useState(undefined);
    const [currentContent, setCurrentContent] = useState(undefined);
    const [upNext, setUpNext] = useState(undefined);
    const [contentSrc, setContentSrc] = useState(undefined);
    const [drmToken, setDrmToken] = useState(undefined);
    const [readyToNext, setReadyToNext] = useState(false);
    const [alreadyChanged, setAlreadyChanged] = useState(false);
    const [playerReady, setPlayerReady] = useState(false);
    const [adStarted, setAdStarted] = useState(false);
    const [startedHeartBeat, setStartedHearBeat] = useState(false);
    const [playerError, setPlayerError] = useState(null);
    const [bingecounter, setBingecounter] = useState(0);
    const [browserOffline, setBrowserOffline] = useState(false);
    const [stalling, setStalling] = useState(false);

    useEffect(() => {
        window.scrollTo({top: 0});

        window.addEventListener('focus', () => {
            !isFocused && setIsFocused(true);
        });

        window.addEventListener('click', () => {
            !isFocused && setIsFocused(true);
        });

        window.addEventListener('offline', e => {
            setBrowserOffline(true);
        });

        window.addEventListener('online', e => {
            setBrowserOffline(false);
        });

        return () => {
            window.turner_metadata.trackAction.push({
                type: 'video',
                subtype: 'Content_Completed',
                data: {
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                },
            });

            setStartedHearBeat(false);
            setPlayerReady(false);
            setAdStarted(false);
            setReadyToNext(false);
            setAlreadyChanged(false);
        };

        // eslint-disable-next-line
    }, [window.location.pathname]);

    useEffect(() => {
        return () => {
            if (userToken !== undefined) {
                let url = `${(config.catalog_settings &&
                    config.catalog_settings.endpoint) ||
                    'https://avs-proxy.dev.aaa.wmgp.io/v1'}/R/${
                    user.catalogLanguage
                }/${
                    config.catalog_settings && config.catalog_settings.platforms
                        ? config.catalog_settings.platforms[0]
                        : 'PCTV_DASH'
                }/${user.catalogTenant ||
                    'CNPLUS_LATAM_LATAM'}/CONTENT/STOP?contentId=${
                    params.episode_id
                }&asJson=Y&type=VOD&bookmark=0&deltaThreshold=0`;

                let headers = new Headers();
                headers.append('Authorization', `Bearer ${userToken}`);

                fetch(url, {headers}).then(res => res.json());
            }
        };

        // eslint-disable-next-line
    }, [userToken, window.location.pathname]);

    useEffect(() => {
        handleConnectionStatus();

        // eslint-disable-next-line
    }, [browserOffline, stalling]);

    useEffect(() => {
        setPageState('loading');
        setCurrentContent(undefined);
        setUpNext(undefined);
        setDrmToken(undefined);
        setContentSrc(undefined);

        if (
            props.episode
                ? props.episode[params.episode_id] === undefined
                : true
        ) {
            dispatch(EpisodeActions.loadEpisode(params.episode_id));
        }

        if (props.videos ? !props.videos[params.episode_id] : true) {
            preparePlayback();
        }

        if (user.jwtToken) {
            dispatch(DrmActions.loadDRMToken(params.episode_id, user.jwtToken));
        }

        //eslint-disable-next-line
    }, [params.episode_id]);

    useEffect(() => {
        if (
            props.episode &&
            props.episode[params.episode_id] &&
            params.episode_id === props.episode[params.episode_id].id.toString()
        ) {
            setCurrentContent(props.episode[params.episode_id]);
        }

        //eslint-disable-next-line
    }, [props.episode, params.episode_id]);

    useEffect(() => {
        if (currentContent !== undefined) {
            loadUpNext();
        }
        //eslint-disable-next-line
    }, [currentContent]);

    useEffect(() => {
        if (currentContent !== undefined) {
            if (currentContent.show) {
                setCurrentShow(currentContent.show);
            } else {
                setCurrentShow({});
            }
        }

        // eslint-disable-next-line
    }, [currentContent]);

    useEffect(() => {
        if (props.videos && props.videos[params.episode_id]) {
            setContentSrc(props.videos[params.episode_id].src);
        }
    }, [props.videos, params.episode_id]);

    useEffect(() => {
        if (props.tokens && props.tokens[params.episode_id]) {
            setDrmToken(props.tokens[params.episode_id]);
        }
    }, [props.tokens, params.episode_id]);

    useEffect(() => {
        if (
            currentContent !== undefined &&
            (upNext !== undefined ||
                currentContent.objectSubtype === 'MOVIE') &&
            pageState !== 'ready' &&
            currentShow !== undefined
        ) {
            setPageState('ready');

            window.turner_metadata.trackAction.push({
                type: 'pageview',
                data: {
                    section: 'Video Playback',
                    subsection: 'nvs',
                    franchise: currentShow.title,
                    contenttype: 'Video Playback',
                    contenttitle: 'Video Playback',
                    presentationtemplate: 'desktop',
                    platform: 'web',
                    domain: 'cartoonito.com',
                    region: 'latam',
                    country:
                        user && user.countryIsoCode && user.countryName
                            ? `${user.countryIsoCode.toLowerCase()}|${
                                  user.countryName
                              }`
                            : 'not loaded',
                    brand: 'cartoon network|cnito',
                    pageviewevent: 1,
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                },
            });
        }

        // eslint-disable-next-line
    }, [currentContent, upNext, currentShow, idp, pageState]);

    useEffect(() => {
        if (pageState === 'ready' && currentContent && currentContent.show) {
            dispatch(episodeStarted(currentContent));
        }

        // eslint-disable-next-line
    }, [pageState]);

    useEffect(() => {
        if (playerReady) {
            const stallIndicator = document.getElementById(
                'yui-stall-indicator',
            );

            if (stallIndicator) {
                Lottie.loadAnimation({
                    container: document.getElementById('yui-stall-indicator'),
                    renderer: 'svg',
                    loop: true,
                    autoplay: true,
                    animationData: CNLoader,
                    isClickToPauseDisabled: true,
                    isStopped: false,
                    isPaused: false,
                });
                stallIndicator.style.width = 200;
                stallIndicator.style.height = 200;
            }
        }

        // eslint-disable-next-line
    }, [playerReady]);

    const handleConnectionStatus = () => {
        if (browserOffline && !navigator.onLine && stalling) {
            setPlayerError({
                title: t('player.offline.title'),
                errorMessage: t('player.offline.message'),
                hideHero: true,
                buttonText: t('player.offline.cta'),
                onClickButton: recheckConnection,
            });
        } else {
            setPlayerError(null);
        }
    };

    const recheckConnection = () => {
        setPlayerError(null);

        setTimeout(handleConnectionStatus, 5000);
    };

    const preparePlayback = async () => {
        let response = await dispatch(
            !user.jwtToken
                ? VideoActions.preparePlaybackFreeContent(params.episode_id)
                : VideoActions.preparePlayback(
                      params.episode_id,
                      user.jwtToken,
                  ),
        );

        if (!!response.data?.data?.playback) {
            setContentSrc(response.data.data.playback.url);
            setDrmToken(response.data.data.playback.drmToken);
        }

        if (
            response.resultCode !== 'OK' &&
            response.errorDescription === 'FRUITION_EXCEED'
        ) {
            dispatch({
                type: 'MODAL_SHOW',
                modal: {
                    visible: true,
                    title: t('video.concurrencyError'),
                    buttons: (
                        <>
                            <Button
                                margin="0 1rem 0 auto"
                                onClick={() => dispatch({type: 'MODAL_CLOSE'})}
                            >
                                Ok
                            </Button>
                        </>
                    ),
                },
            });

            setPlayerError({errorMessage: t('video.concurrencyError')});
        } else if (response.errorDescription === 'WM_100') {
            setPlayerError({errorMessage: t('onnow.geoblockError')});
        }
    };

    const loadUpNext = async () => {
        let query = graphqlQueryBuild(
            'upNext',
            'id title image duration episode season description',
            `id: ${currentContent.id}`,
        );

        let upnext_response = await api
            .get('', {
                params: {
                    query,
                },
            })
            .then(res => res)
            .catch(err => err.response);

        if (
            upnext_response.status === 200 &&
            upnext_response.data?.data?.upNext
        ) {
            upnext_response.data.data.upNext.map(vod => {
                vod.objectSubtype =
                    vod.season >= 1 && vod.episode >= 1 ? 'VIDEO' : 'MOVIE';

                return vod;
            });

            setUpNext(upnext_response.data?.data?.upNext);
        } else {
            setUpNext([]);
        }
    };

    const handlePlayNextEpisode = () => {
        setReadyToNext(true);
    };

    useEffect(() => {
        if (upNext && upNext.length > 0 && readyToNext && !alreadyChanged) {
            setTimeout(() => {
                history.push(`/play/${upNext[0].id}`);
            }, 2000);
            setAlreadyChanged(true);
        }
        // eslint-disable-next-line
    }, [readyToNext]);

    useEffect(() => {
        if (playerReady) {
            sendPlayerReadyAnalyticsCall();
        }

        // eslint-disable-next-line
    }, [playerReady]);

    useEffect(() => {
        if (playerReady && adStarted) {
            sendPlayerReadyAnalyticsCall();

            window.turner_metadata.trackAction.push({
                type: 'video',
                subtype: 'Ad_Started',
                data: {
                    ad_id: 'pre-roll-1' /* ad identifier */,
                    ad_duration: 0 /* ad duration in seconds */,
                    ad_type:
                        'preroll' /* ad type (e.g. preroll, midroll, postroll) */,
                    platform: 'web',
                    content_name:
                        currentContent.objectSubtype === 'MOVIE'
                            ? currentContent.title
                            : t('video.titleFormat')
                                  .replace('{season}', currentContent.season)
                                  .replace('{episode}', currentContent.episode),
                    content_id: currentContent.id.toString(),
                    content_type:
                        currentContent.objectSubtype === 'MOVIE'
                            ? 'movies'
                            : 'vod',
                    content_showName: currentContent.title,
                    content_seasonNumber: currentContent.season,
                    content_episodeNumber: currentContent.episode,

                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                    logintype: user && user.user_type,
                },
            });
        }

        // eslint-disable-next-line
    }, [playerReady, adStarted]);

    const memoizedPlayerComponent = React.useMemo(() => {
        if (
            (contentSrc === undefined ||
                drmToken === undefined ||
                pageState !== 'ready') &&
            !playerError
        ) {
            return <Loader />;
        } else {
            let adTagUrl =
                'https://pubads.g.doubleclick.net/gampad/ads?sz=970x250%7C320x50%7C300x250%7C728x90%7C640x480&iu=/99911224/ci_[countryname]la&env=vp&impl=s&gdfp_req=1&output=vast&unviewed_position_start=1&description_url=http%3A%2F%2Fwww.cartoonitola.com&correlator=';

            if (adTagUrl && adTagUrl.indexOf('[countryname]')) {
                if (user.countryIsoCode) {
                    adTagUrl = adTagUrl.replace(
                        '[countryname]',
                        ['ar', 'br', 'co', 'cl', 'mx', 've'].includes(
                            user.countryIsoCode.toLowerCase(),
                        )
                            ? user.countryIsoCode.toLowerCase()
                            : 'la',
                    );
                } else {
                    adTagUrl = undefined;
                }
            }

            return (
                <>
                    {!playerReady && !playerError && <Loader />}

                    {playerError && <PlayerError {...playerError} />}

                    <div
                        style={
                            !playerReady || !!playerError
                                ? {display: 'none'}
                                : {}
                        }
                    >
                        <YorhaBitmovin
                            title={
                                currentContent.objectSubtype === 'MOVIE'
                                    ? `${currentContent.title}`
                                    : `[${currentShow && currentShow.title}] S${
                                          currentContent.season
                                      }:E${currentContent.episode}`
                            }
                            ispToken={drmToken}
                            license="59be50ed-f8b4-4ee3-8ec4-c24560a462ff"
                            preferredLanguage={
                                window.location.origin.includes('.br')
                                    ? 'POR'
                                    : 'SPA'
                            }
                            source={
                                config.user_device === 'IPHONE'
                                    ? {
                                          hls: contentSrc,
                                      }
                                    : {
                                          dash: contentSrc,
                                      }
                            }
                            oneTrust={true}
                            autoplay={config.user_device !== 'IPHONE'}
                            muted={
                                config.user_device === 'IPHONE'
                                    ? false
                                    : !isFocused
                            }
                            chromecast={false}
                            chromecastReceiverId={
                                config.player_settings.chromecast.receiverId
                            }
                            chromecastParams={{
                                metadata: {
                                    episode: currentContent.episode,
                                    season: currentContent.season,
                                    title: currentContent.title,
                                    seriesTitle: currentContent.description,
                                },
                                streamType:
                                    currentContent.objectSubtype === 'MOVIE'
                                        ? 'MOVIE'
                                        : 'VOD',
                                customData: {
                                    contentId: currentContent.id,
                                    brand: 'CNPLUS',
                                    device: 'PCTV',
                                    JWTToken: user.jwtToken,
                                    region: user.catalogTenant,
                                },
                            }}
                            customErrors={{
                                1400: 'AAAHHHH NOOOOOOOO',
                            }}
                            customStyle="https://cn.i.cdn.ti-platform.com/static/cnplus/cartoon-plus-reface.css"
                            widevineLAUrl={
                                config.player_settings.drm.widevine.LA_URL
                            }
                            fairplayLAUrl={
                                config.player_settings.drm.fairplay &&
                                config.player_settings.drm.fairplay.LA_URL
                            }
                            fairplayCertificateUrl={
                                config.player_settings.drm.fairplay &&
                                config.player_settings.drm.fairplay
                                    .certificateURL
                            }
                            youboraConfig={
                                currentContent && {
                                    accountCode:
                                        config.player_settings.analytics &&
                                        config.player_settings.analytics
                                            .youboraAccount
                                            ? config.player_settings.analytics
                                                  .youboraAccount
                                            : 'turnerlatam',
                                    contentIsLive: false,
                                    contentTitle:
                                        currentContent.objectSubtype === 'MOVIE'
                                            ? `${currentContent.title}`
                                            : `[${currentShow &&
                                                  currentShow.title}] S${
                                                  currentContent.season
                                              }:E${currentContent.episode}`,
                                    contentId: currentContent.id,
                                    username: idp,
                                    extraparams: {
                                        extraparam1: 'Cartoon Network',
                                        extraparam2: currentContent.season,
                                        extraparam3: currentContent.episode,
                                    },
                                }
                            }
                            localeSettings={
                                tve && {
                                    localeLanguage: window.location.origin.includes(
                                        '.br',
                                    )
                                        ? 'pt-BR'
                                        : 'es-ES',
                                }
                            }
                            adTagUrl={adTagUrl}
                            eventHandlers={{
                                onReady: () => {
                                    config.user_device === 'IPHONE' &&
                                        hideStallIndicator();

                                    setPlayerReady(true);
                                },
                                onPlay: () => {
                                    if (!playerReady) {
                                        setPlayerReady(true);
                                    }
                                },
                                onAdStarted: event => {
                                    setPlayerReady(true);

                                    setAdStarted(true);
                                },
                                onAdSkipped: () => {
                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Ad_Skipped',
                                        data: {
                                            platform: 'web',
                                            content_name:
                                                currentContent.objectSubtype ===
                                                'MOVIE'
                                                    ? currentContent.title
                                                    : t('video.titleFormat')
                                                          .replace(
                                                              '{season}',
                                                              currentContent.season,
                                                          )
                                                          .replace(
                                                              '{episode}',
                                                              currentContent.episode,
                                                          ),
                                            content_id: currentContent.id.toString(),
                                            content_type:
                                                currentContent.objectSubtype ===
                                                'MOVIE'
                                                    ? 'movies'
                                                    : 'vod',
                                            content_showName:
                                                currentContent.title,
                                            content_seasonNumber:
                                                currentContent.season,
                                            content_episodeNumber:
                                                currentContent.episode,

                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                            logintype: user.user_type,
                                        },
                                    });
                                },

                                onAdFinished: () => {
                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Ad_Finished',
                                        data: {
                                            platform: 'web',
                                            content_name:
                                                currentContent.objectSubtype ===
                                                'MOVIE'
                                                    ? currentContent.title
                                                    : t('video.titleFormat')
                                                          .replace(
                                                              '{season}',
                                                              currentContent.season,
                                                          )
                                                          .replace(
                                                              '{episode}',
                                                              currentContent.episode,
                                                          ),
                                            content_id: currentContent.id.toString(),
                                            content_type:
                                                currentContent.objectSubtype ===
                                                'MOVIE'
                                                    ? 'movies'
                                                    : 'vod',
                                            content_showName:
                                                currentContent.title,
                                            content_seasonNumber:
                                                currentContent.season,
                                            content_episodeNumber:
                                                currentContent.episode,

                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                            logintype: user.user_type,
                                        },
                                    });
                                },

                                onPlaying: () => {
                                    setReadyToNext(false);

                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Media_Started',
                                        data: {
                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                        },
                                    });
                                },

                                onTimeChange: e => {
                                    window.turner_metadata.currentTime = parseInt(
                                        e.time,
                                    );
                                },

                                onStallStarted: () => {
                                    setStalling(true);

                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Media_Buffering_Started',
                                        data: {
                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                        },
                                    });
                                },

                                onStallEnded: () => {
                                    setStalling(false);

                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Media_Buffering_Finished',
                                        data: {
                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                        },
                                    });
                                },

                                onPause: () => {
                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Media_Paused',
                                        data: {
                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                        },
                                    });
                                },

                                onPlaybackFinished: () => {
                                    window.turner_metadata.trackAction.push({
                                        type: 'video',
                                        subtype: 'Media_Finished',
                                        data: {
                                            authstate:
                                                user && user.jwtToken
                                                    ? 'authenticated'
                                                    : 'unauthenticated',
                                            mvpd: idp || 'idp not loaded',
                                        },
                                    });

                                    handlePlayNextEpisode();
                                },
                            }}
                        />
                    </div>
                </>
            );
        }

        // eslint-disable-next-line
    }, [
        contentSrc,
        drmToken,
        playerReady,
        playerError,
        currentContent,
        currentShow,
        pageState,
        startedHeartBeat,
        bingecounter,
        isFocused,
    ]);

    const sendPlayerReadyAnalyticsCall = () => {
        if (!startedHeartBeat) {
            currentContent.show && dispatch(episodeFinished(currentContent));

            window.turner_metadata.trackAction.push({
                type: 'video',
                subtype: 'Player_Ready',
                data: {
                    playerid: '',
                    content_duration: currentContent.duration,
                    content_dataCreated: '' /* YYYY-MM-DD (e.g. 2018-08-30) */,
                    content_dataAired: '' /* YYYY-MM-DD (e.g. 2018-08-30) */,
                    content_name:
                        currentContent.objectSubtype === 'MOVIE'
                            ? currentContent.title
                            : t('video.titleFormat')
                                  .replace('{season}', currentContent.season)
                                  .replace('{episode}', currentContent.episode),
                    content_id: currentContent.id.toString(),
                    content_type:
                        currentContent.objectSubtype === 'MOVIE'
                            ? 'movies'
                            : 'vod',
                    content_showName: currentContent.title,
                    content_seasonNumber: currentContent.season,
                    content_episodeNumber: currentContent.episode,
                    content_genre: currentContent.genres,
                    content_rating:
                        '' /* rating (e.g. tvy, tvg, tvpg, tvm, etc.) */,
                    content_originator:
                        '' /* originator (e.g. warnermedia, sony, disney, etc.) */,
                    content_network:
                        '' /* network (e.g. fox, brabo, espn, etc.) */,
                    content_mvpd:
                        '' /* mvpd (e.g. comcast, directv, dish, etc.) */,
                    content_authorized: '' /* true or false */,
                    day_part:
                        '' /* time of day (e.g. morning, daytime, primetime, etc.) */,
                    content_feed:
                        '' /* feed (e.g. east-hd, west-hd, east-sd, etc.) */,
                    authstate:
                        user && user.jwtToken
                            ? 'authenticated'
                            : 'unauthenticated',
                    mvpd: idp || 'idp not loaded',
                    logintype: user.user_type,
                    language: currentContent.metadataLanguage,
                    franchise: currentContent.title,
                    usercountry:
                        user && user.countryName
                            ? user.countryName
                            : 'not loaded',
                    avsid: currentContent.id,
                    houseid: currentContent.externalId,
                    producer: currentContent.contentProvider,
                    bingecounter: bingecounter + 1,
                    brand: 'cnito',
                },
            });

            setBingecounter(bingecounter + 1);
            setStartedHearBeat(true);
        }
    };

    const hideStallIndicator = () => {
        const stallIndicator = document.querySelector('#yui-stall-indicator');

        const pauseOverlay = document.querySelector('#yui-overlay-play');

        if (stallIndicator) {
            stallIndicator.classList.add('playing');
            stallIndicator.classList.remove('buffering');
        }

        if (pauseOverlay) {
            pauseOverlay
                .querySelector('div:last-child')
                .classList.remove('hidden');
            pauseOverlay
                .querySelector('div:last-child')
                .classList.remove('player-playing');
            pauseOverlay
                .querySelector('div:last-child')
                .classList.add('player-paused');
        }
    };

    return (
        <>
            <GlobalStyle />
            <Helmet>
                <title>Cartoon Network App</title>
            </Helmet>
            <Container>
                {pageState !== 'ready' ? (
                    <Loader />
                ) : (
                    <>
                        {currentContent &&
                            (currentContent.objectSubtype !== 'MOVIE'
                                ? currentShow
                                : true) && (
                                <Helmet>
                                    <title>
                                        {currentContent.objectSubtype !==
                                        'MOVIE'
                                            ? `Assista ${capitalize(
                                                  currentShow.title,
                                                  true,
                                              )} ${t('video.titleFormat')
                                                  .replace(
                                                      '{season}',
                                                      currentContent.season,
                                                  )
                                                  .replace(
                                                      '{episode}',
                                                      currentContent.episode,
                                                  )} | Cartoon Network App`
                                            : `${capitalize(
                                                  currentContent.title,
                                                  true,
                                              )} | Cartoon Network App`}
                                    </title>
                                    <meta
                                        name="description"
                                        content={
                                            currentContent.objectSubtype !==
                                            'MOVIE'
                                                ? `${capitalize(
                                                      currentShow.title,
                                                      true,
                                                  )} ${capitalize(
                                                      t('shows.season'),
                                                  )} ${
                                                      currentContent.season
                                                  }, ${t(
                                                      'video.episode',
                                                  ).toLowerCase()} ${
                                                      currentContent.episode
                                                  }. ${
                                                      currentContent.description
                                                  }`
                                                : `${currentContent.description}`
                                        }
                                    ></meta>
                                </Helmet>
                            )}

                        <VideoPlayerContainer>
                            {memoizedPlayerComponent}
                        </VideoPlayerContainer>
                    </>
                )}
            </Container>
        </>
    );
};

const GlobalStyle = createGlobalStyle`
    @media screen and (max-width: 1024px) {
        body {
            padding-bottom: 0px;
        }
    }

    /* Yorha player wrapper styles */


    yui-play-button{

    }
`;

const Container = styled(PageBodyWrapper)`
    width: 100%;
    height: calc(100vh - 80px);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    padding: 0;
    box-sizing: border-box;

    .error-display {
        display: none;
    }

    ${notWebMediaQuery} {
        height: 100vh;
        padding-top: 80px;
    }

    #yui-wrapper #yui-stall-indicator {
        width: 400px;
        margin: auto;
        right: 0;
    }
`;

const VideoPlayerContainer = styled.div`
    width: 100%;
    height: 100%;
    flex: 1;
    border-width: 1px;
    border-color: red;

    .bitmovinplayer-container {
        width: 100% !important;
        height: 100% !important;
    }

    & > div {
        height: calc(100% - 100px);
        position: relative;
    }
`;

export default CartoonitoPlayer;
